import type { ReactNode } from 'react';
import cx from 'clsx';
import { useRouter } from 'next/router';
import { trackEvent } from '@kcd/event-tracker';

import { useAdultGuardClick } from '~/domains/products/useShowAdultBadge';
import type { BottomSheetOption } from '~/store/global/useProductBottomSheetStore';
import useProductBottomSheetStore from '~/store/global/useProductBottomSheetStore';
import useProductOptionStore from '~/store/global/useProductOptionStore';
import type { OAISchema } from '~/types/openapi';

import { useRegisterMembershipDialog } from '../plusMembership/hooks';

import { CartIcon } from '~/assets/icons';

export type UpdateQuantityReqParams =
  OAISchema['MarketUpdateQuickShoppingCartItemRequest'];

type Props = {
  item: OAISchema['MarketSelectProductResponseV2'];
  className?: string;
  onClick?: () => void;
  bottomSheetOption?: BottomSheetOption;
  children?: ReactNode;
};

function ShoppingCartButton({
  item,
  className,
  onClick,
  bottomSheetOption,
  children,
}: Props) {
  const { openBottomSheet } = useProductBottomSheetStore();
  const { setOfferId } = useProductOptionStore();

  const registerMembershipDialog = useRegisterMembershipDialog();
  const { query } = useRouter();
  const guardAdultClick = useAdultGuardClick(item.adultOnly);

  const handleClick: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // MKPC-3616: 미성년이 19금 상품 장바구니 담기 시도 → 토스트 후 차단(목록 클릭과 동일)
    if (guardAdultClick(e)) return;

    onClick?.();

    trackEvent('clicked purchaseButton in 마켓', {
      placement: '상품목록',
      productName: item.productName,
      offerId: item.offerId,
      path: window.location.pathname,
      searchText: decodeURIComponent(query.searchText as string) || '',
      showCashnotePlusSuggestion: item.showCashnotePlusSuggestion,
    });

    if (item.showCashnotePlusSuggestion) {
      void registerMembershipDialog('&salesTarget=CASHNOTE_PLUS');
      return;
    }

    openBottomSheet({ ...bottomSheetOption });
    setOfferId(item.offerId!);
  };

  return (
    <div
      className={cx([
        'bg-transparent',
        'active:bg-transparent',
        !children && 'w-32 h-32',
        className,
      ])}
    >
      <button
        type="button"
        className="flex w-full items-center justify-center"
        onClick={handleClick}
        data-testid="cart_shopping-cart-button"
      >
        {children || <CartIcon />}
      </button>
    </div>
  );
}

export default ShoppingCartButton;
